import { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useQuery } from '@tanstack/react-query';
import { useMergeCandidates } from '../../hooks/useSearch.js';
import { api } from '../../lib/api.js';
import { Icon } from '../Icon.js';
import { Button } from '../ui/Button.js';
import { Input } from '../ui/Input.js';
import { Pressable } from '../ui/Pressable.js';
import { EmptyState } from '../ui/EmptyState.js';

interface ContactStub {
  id: number;
  name: string | null;
  email: string | null;
}

interface MergeCandidate {
  id: number;
  fullName: string | null;
  primaryEmail: string | null;
  businessName: string | null;
  score: number;
  matchReason: string;
}

interface Props {
  currentContactId: number;
  currentContactName: string;
  onMergeConfirmed: (targetId: number) => void;
  onCancel: () => void;
  isMerging?: boolean;
}

function useContactSearch(q: string) {
  return useQuery<ContactStub[]>({
    queryKey: ['contact-search-merge', q],
    queryFn: () => api.get<ContactStub[]>(`/api/contacts/search?q=${encodeURIComponent(q)}&limit=8`),
    enabled: q.length >= 2,
    staleTime: 10000,
  });
}

export function MergeContactSelector({ currentContactId, currentContactName: _currentContactName, onMergeConfirmed, onCancel, isMerging }: Props) {
  const { t } = useTranslation();
  const [searchQ, setSearchQ] = useState('');
  const [debouncedQ, setDebouncedQ] = useState('');
  const [selected, setSelected] = useState<{ id: number; name: string } | null>(null);

  useEffect(() => {
    const timer = setTimeout(() => setDebouncedQ(searchQ), 250);
    return () => clearTimeout(timer);
  }, [searchQ]);

  const { data: candidates = [], isLoading: loadingCandidates } = useMergeCandidates(currentContactId);
  const { data: searchResults = [], isFetching: searching } = useContactSearch(debouncedQ);

  const matchLabel = (reason: string) =>
    t(`contacts.mergeCandidates.${reason}`, { defaultValue: reason });

  if (selected) {
    return (
      <div className="p-3 rounded" style={{ background: 'var(--evo-bg-2)', border: '1px solid var(--evo-stroke)' }}>
        <p className="text-xs mb-3" style={{ color: 'var(--evo-fg-2)' }}>
          {t('contacts.mergeCandidates.previewConfirm', { name: selected.name })}
        </p>
        <div className="flex gap-2">
          <Button variant="solid" tone="danger" size="sm" loading={!!isMerging} disabled={!!isMerging} onClick={() => onMergeConfirmed(selected.id)}>
            {t('contacts.mergeCandidates.confirm')}
          </Button>
          <Button variant="outline" tone="muted" size="sm" disabled={!!isMerging} onClick={() => setSelected(null)}>
            {t('contacts.mergeCandidates.cancel')}
          </Button>
        </div>
      </div>
    );
  }

  const hasCandidates = candidates.length > 0;

  return (
    <div className="p-3 rounded mb-4" style={{ background: 'var(--evo-bg-2)', border: '1px solid var(--evo-stroke)' }}>
      {/* Suggested candidates */}
      {loadingCandidates && (
        <p className="text-xs mb-2" style={{ color: 'var(--evo-fg-3)' }}>Buscando duplicados...</p>
      )}
      {!loadingCandidates && hasCandidates && (
        <div className="mb-3">
          <p className="text-xs mb-2" style={{ color: 'var(--evo-fg-3)', fontWeight: 500 }}>
            {t('contacts.merge')}
          </p>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
            {(candidates as MergeCandidate[]).map(c => (
              <Pressable
                key={c.id}
                fullWidth
                onPress={() => setSelected({ id: c.id, name: c.fullName ?? c.primaryEmail ?? String(c.id) })}
              >
                <div className="flex items-center justify-between gap-2 text-left px-2 py-1.5 rounded" style={{ border: '1px solid var(--evo-stroke)' }}>
                  <div style={{ minWidth: 0 }}>
                    <p className="text-xs truncate font-medium" style={{ color: 'var(--evo-fg-2)' }}>
                      {c.fullName ?? c.businessName ?? '—'}
                    </p>
                    {c.primaryEmail && (
                      <p className="text-xs truncate" style={{ color: 'var(--evo-fg-3)' }}>{c.primaryEmail}</p>
                    )}
                  </div>
                  <span
                    className="text-xs shrink-0 px-1.5 py-0.5 rounded"
                    style={{ background: 'var(--evo-cyan)22', color: 'var(--evo-cyan)', border: '1px solid var(--evo-cyan)44' }}
                  >
                    {matchLabel(c.matchReason)}
                  </span>
                </div>
              </Pressable>
            ))}
          </div>
        </div>
      )}

      {/* Manual search */}
      {!loadingCandidates && !hasCandidates && (
        <EmptyState size="sm" reserveSpace title={t('contacts.mergeCandidates.noCandidates')} />
      )}
      {hasCandidates && (
        <p className="text-xs mb-1.5" style={{ color: 'var(--evo-fg-4)', fontSize: 10 }}>
          {t('contacts.mergeCandidates.noCandidates')}
        </p>
      )}
      <div className="relative">
        <Input
          type="text"
          size="sm"
          fullWidth
          value={searchQ}
          onChange={v => setSearchQ(v)}
          placeholder={t('contacts.mergeCandidates.searchPlaceholder')}
          iconAfter={searching ? <span style={{ fontSize: 10, color: 'var(--evo-fg-4)' }}>...</span> : undefined}
        />
        {debouncedQ.length >= 2 && searchResults.length > 0 && (
          <div
            className="absolute left-0 right-0 z-10 rounded overflow-hidden"
            style={{ top: '100%', marginTop: 2, background: 'var(--evo-bg-3)', border: '1px solid var(--evo-stroke)', maxHeight: 180, overflowY: 'auto' }}
          >
            {(searchResults)
              .filter(r => r.id !== currentContactId)
              .map(r => (
                <Pressable
                  key={r.id}
                  fullWidth
                  onPress={() => {
                    setSelected({ id: r.id, name: r.name ?? r.email ?? String(r.id) });
                    setSearchQ('');
                    setDebouncedQ('');
                  }}
                >
                  <div className="px-2 py-1.5 flex items-center gap-2" style={{ color: 'var(--evo-fg-2)', borderBottom: '1px solid var(--evo-stroke)' }}>
                    <Icon name="contacts" size={11} />
                    <span className="text-xs truncate">{r.name ?? '—'}</span>
                    {r.email && <span className="text-xs truncate" style={{ color: 'var(--evo-fg-3)' }}>{r.email}</span>}
                  </div>
                </Pressable>
              ))}
          </div>
        )}
      </div>
      <Button variant="ghost" tone="muted" size="sm" onClick={onCancel}>
        {t('contacts.mergeCandidates.cancel')}
      </Button>
    </div>
  );
}
